import { useState, useMemo } from 'react';
import type { AnalysisType } from '../../shared/types';
import { ANALYSIS_OPTIONS } from '../../shared/types';
import type { SidebarItem } from '../hooks/useAnalysisJobs';
import { useAuth } from '../contexts/AuthContext';
import AnalysisIcon from './ui/AnalysisIcons';
import Avatar from './ui/Avatar';

export type SidebarNav = 'new' | 'history' | 'pricing' | 'account' | null;

interface SidebarProps {
  history: SidebarItem[];
  onViewHistory: (item: SidebarItem) => void;
  onRenameHistory: (id: string, newTitle: string) => void;
  onDeleteHistory: (id: string) => void;
  activeItemId?: string | null;
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  onAccountClick: () => void;
  onHomeClick?: () => void;
  /** Navigation principale — section « Général » de la charte. */
  onHistoriqueClick?: () => void;
  onPricingClick?: () => void;
  activeNav?: SidebarNav;
}

const formatDate = (iso: string) => {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return 'Hier';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

/** Score → pastille du nuancier (kaki / jaune / terracotta / brique) */
const verdictColorFromScore = (score: number) => {
  if (score >= 80) return 'text-khaki-400';
  if (score >= 60) return 'text-sun-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
};

/* Entrée de navigation — barre rose à gauche quand active, comme la charte. */
function NavItem({
  label, active, collapsed, onClick, children,
}: {
  label: string;
  active: boolean;
  collapsed: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-current={active ? 'page' : undefined}
      className={`group relative flex w-full items-center rounded-xl py-2.5 text-sm transition-colors ${
        collapsed ? 'justify-center px-0' : 'gap-3 px-3'
      } ${active ? 'text-rose-pastel-400' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
    >
      {active && (
        <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-rose-pastel-400" />
      )}
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">{children}</span>
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  );
}

export default function Sidebar({
  history,
  onViewHistory,
  onRenameHistory,
  onDeleteHistory,
  activeItemId,
  isMobileOpen,
  setIsMobileOpen,
  isCollapsed,
  setIsCollapsed,
  onAccountClick,
  onHomeClick,
  onHistoriqueClick,
  onPricingClick,
  activeNav = null,
}: SidebarProps) {
  const { features, user, organization, signOut } = useAuth();
  const hasHistoryAccess = features.history || (organization?.creditBalance ?? 0) > 0;
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<AnalysisType | 'all'>('all');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Catégories effectivement présentes dans l'historique
  const usedCategories = useMemo(() => {
    const keys = new Set(history.map(h => h.analysisType));
    return ANALYSIS_OPTIONS.filter(opt => keys.has(opt.key));
  }, [history]);

  const filteredHistory = useMemo(() => {
    return history.filter(item => {
      const matchesFilter = activeFilter === 'all' || item.analysisType === activeFilter;
      const matchesSearch = !search || item.title.toLowerCase().includes(search.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [history, activeFilter, search]);

  const grouped = useMemo(() => {
    const groups: Record<string, SidebarItem[]> = {};
    filteredHistory.forEach(item => {
      const label = formatDate(item.timestamp);
      if (!groups[label]) groups[label] = [];
      groups[label].push(item);
    });
    return groups;
  }, [filteredHistory]);

  const planLabel = ({ free: 'Gratuit', beta: 'Beta Partner', pro: 'Pro', premium: 'Premium', enterprise: 'Enterprise' } as Record<string, string>)[organization?.plan ?? 'free'] ?? 'Gratuit';

  return (
    <>
      {/* Voile mobile */}
      <div
        className={`fixed inset-0 z-30 bg-ink/50 backdrop-blur-sm transition-opacity md:hidden ${
          isMobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setIsMobileOpen(false)}
      />

      <aside
        className={`panel-ink fixed z-40 flex h-full flex-col rounded-3xl transition-all duration-300 md:relative md:h-full
          ${isMobileOpen ? 'inset-y-3 left-3 w-72 translate-x-0' : '-translate-x-[110%]'}
          md:translate-x-0 ${isCollapsed ? 'md:w-[76px]' : 'md:w-[248px]'}`}
      >
        {/* Chevron de repli — pastille rose en débord, détail de la charte */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Déplier le menu' : 'Replier le menu'}
          className="absolute -right-2.5 top-[26px] z-10 hidden h-6 w-6 items-center justify-center rounded-full bg-rose-pastel-400 text-ink transition-transform hover:scale-110 md:flex"
        >
          <svg
            className={`h-3.5 w-3.5 transition-transform duration-300 ${isCollapsed ? '' : 'rotate-180'}`}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        <div className="flex h-full flex-col overflow-hidden">
          {/* ── Logo ── */}
          <div className={`flex h-[68px] shrink-0 items-center ${isCollapsed ? 'justify-center px-0' : 'px-5'}`}>
            <button
              onClick={onHomeClick}
              title="Accueil"
              className="flex min-w-0 items-center gap-2 rounded-md transition-opacity hover:opacity-70"
            >
              <img
                src="/visaly_logo.svg"
                alt="Visaly"
                className={`shrink-0 select-none ${isCollapsed ? 'h-7 w-7' : 'h-6 w-6'}`}
                draggable={false}
              />
              {!isCollapsed && (
                <span className="font-display truncate text-[1.55rem] leading-none text-white">Visaly</span>
              )}
            </button>
          </div>

          {/* ── Navigation principale ── */}
          <div className={`shrink-0 ${isCollapsed ? 'px-3' : 'px-3'}`}>
            {!isCollapsed && (
              <p className="mb-1 px-3 text-[11px] font-medium uppercase tracking-[0.14em] text-white/35">
                Général
              </p>
            )}
            <NavItem label="Nouvelle analyse" active={activeNav === 'new'} collapsed={isCollapsed} onClick={onHomeClick}>
              <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="2" /><rect x="14" y="3" width="7" height="7" rx="2" />
                <rect x="3" y="14" width="7" height="7" rx="2" /><rect x="14" y="14" width="7" height="7" rx="2" />
              </svg>
            </NavItem>
            <NavItem label="Historique" active={activeNav === 'history'} collapsed={isCollapsed} onClick={onHistoriqueClick}>
              <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="4" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
            </NavItem>
            <NavItem label="Abonnement" active={activeNav === 'pricing'} collapsed={isCollapsed} onClick={onPricingClick}>
              <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <circle cx="7" cy="7" r="1.2" />
              </svg>
            </NavItem>
            <NavItem label="Mon compte" active={activeNav === 'account'} collapsed={isCollapsed} onClick={onAccountClick}>
              <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6h.09A1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </NavItem>
          </div>

          {/* ── Bouton d'action ── */}
          <div className={`mt-4 shrink-0 ${isCollapsed ? 'px-3' : 'px-4'}`}>
            <button
              onClick={onHomeClick}
              title="Nouvelle analyse"
              className={`flex w-full items-center rounded-full bg-white text-sm font-medium text-ink transition-transform hover:scale-[1.02] active:scale-100 ${
                isCollapsed ? 'justify-center p-2.5' : 'justify-center gap-2 px-4 py-2.5'
              }`}
            >
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {!isCollapsed && <span>Nouvelle analyse</span>}
            </button>
          </div>

          {/* ── Historique ── */}
          <nav className="flex flex-grow flex-col gap-1 overflow-hidden p-3 pt-4">
            {!hasHistoryAccess ? (
              !isCollapsed && (
                <div className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center text-sm text-white/40">
                  <svg className="mb-3 h-8 w-8 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="mb-1 font-medium text-white/70">Historique désactivé</p>
                  <p>Mettez à niveau votre abonnement pour conserver vos analyses.</p>
                </div>
              )
            ) : (
              <>
                {!isCollapsed && (
                  <>
                    <p className="mb-2 px-3 text-[11px] font-medium uppercase tracking-[0.14em] text-white/35">
                      Mes analyses
                    </p>

                    {/* Recherche — champ pilule, comme la barre de la charte */}
                    <div className="glow-input relative mb-2 rounded-full">
                      <svg
                        className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-white/40"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}
                      >
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                      </svg>
                      <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Rechercher…"
                        className="w-full rounded-full border border-white/10 bg-white/5 py-2 pl-10 pr-3 text-sm text-white placeholder-white/35 outline-none focus:border-transparent"
                      />
                    </div>

                    {usedCategories.length > 0 && (
                      <div className="glow-input mb-3 rounded-full">
                        <select
                          value={activeFilter}
                          onChange={e => setActiveFilter(e.target.value as AnalysisType | 'all')}
                          className="w-full rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 outline-none [&>option]:bg-slate-900 [&>option]:text-white"
                        >
                          <option value="all">Toutes les analyses</option>
                          {usedCategories.map(opt => (
                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}

                {/* Liste */}
                <div className="scrollbar-visaly scrollbar-ink flex-1 space-y-4 overflow-y-auto pb-2">
                  {history.length === 0 ? (
                    <div className={`px-2 py-8 text-center text-sm text-white/35 ${isCollapsed ? 'hidden' : ''}`}>
                      Vos analyses apparaîtront ici.
                    </div>
                  ) : filteredHistory.length === 0 ? (
                    <div className={`px-2 py-8 text-center text-sm text-white/35 ${isCollapsed ? 'hidden' : ''}`}>
                      Aucun résultat.
                    </div>
                  ) : isCollapsed ? (
                    <div className="space-y-1">
                      {history.slice(0, 10).map(item => (
                        <button
                          key={item.id}
                          onClick={() => onViewHistory(item)}
                          title={item.title}
                          className={`flex w-full items-center justify-center rounded-xl p-2 transition-colors ${
                            activeItemId === item.id ? 'bg-white/15 text-white' : 'text-white/45 hover:bg-white/8 hover:text-white'
                          }`}
                        >
                          <AnalysisIcon type={item.analysisType} className="h-4 w-4" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    (Object.entries(grouped) as [string, SidebarItem[]][]).map(([dateLabel, items]) => (
                      <div key={dateLabel}>
                        <p className="mb-1 px-3 text-[11px] font-medium uppercase tracking-[0.12em] text-white/30">
                          {dateLabel}
                        </p>
                        <div className="space-y-0.5">
                          {items.map(item => (
                            <div key={item.id} className="group/item relative">
                              {renamingId === item.id ? (
                                <div className="px-2 py-1">
                                  <input
                                    autoFocus
                                    value={renameValue}
                                    onChange={e => setRenameValue(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        onRenameHistory(item.id, renameValue.trim() || item.title);
                                        setRenamingId(null);
                                      }
                                      if (e.key === 'Escape') setRenamingId(null);
                                    }}
                                    onBlur={() => {
                                      onRenameHistory(item.id, renameValue.trim() || item.title);
                                      setRenamingId(null);
                                    }}
                                    className="w-full rounded-lg border border-rose-pastel-400 bg-white/10 px-2 py-1 text-sm text-white outline-none"
                                  />
                                </div>
                              ) : (
                                <button
                                  onClick={() => onViewHistory(item)}
                                  className={`w-full rounded-xl px-3 py-2 pr-16 text-left text-sm transition-colors ${
                                    activeItemId === item.id ? 'bg-white/12' : 'hover:bg-white/6'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    {item.status === 'running' || item.status === 'queued' ? (
                                      <svg className="h-2.5 w-2.5 shrink-0 animate-spin text-rose-pastel-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" />
                                      </svg>
                                    ) : item.status === 'error' ? (
                                      <span className="h-2 w-2 shrink-0 rounded-full bg-red-400" />
                                    ) : !item.read ? (
                                      <span className="h-2 w-2 shrink-0 rounded-full bg-rose-pastel-400" title="Non lu" />
                                    ) : (
                                      <span className="h-2 w-2 shrink-0" />
                                    )}
                                    <p className={`truncate font-medium ${activeItemId === item.id ? 'text-white' : 'text-white/80'}`}>
                                      {item.title}
                                    </p>
                                  </div>
                                  <p className="ml-4 mt-0.5 truncate text-xs text-white/40">
                                    {item.analysisLabel}
                                    {item.status === 'running' && <>{' '}&middot;{' '}<span className="text-rose-pastel-400">analyse en cours…</span></>}
                                    {item.status === 'queued' && <>{' '}&middot;{' '}<span className="text-rose-pastel-400">en attente…</span></>}
                                    {item.status === 'error' && <>{' '}&middot;{' '}<span className="text-red-400">échec</span></>}
                                    {item.status === 'done' && item.score >= 0 && (
                                      <>{' '}&middot;{' '}<span className={verdictColorFromScore(item.score)}>{item.score}/100</span></>
                                    )}
                                  </p>
                                </button>
                              )}

                              {/* Actions au survol */}
                              {renamingId !== item.id && item.status !== 'running' && item.status !== 'queued' && (
                                <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5 opacity-0 transition-opacity group-hover/item:opacity-100">
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      setRenamingId(item.id);
                                      setRenameValue(item.title);
                                    }}
                                    title="Renommer"
                                    className="rounded-full p-1.5 text-white/45 hover:bg-white/12 hover:text-white"
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      if (window.confirm("Supprimer cette analyse de l'historique ?")) {
                                        onDeleteHistory(item.id);
                                      }
                                    }}
                                    title="Supprimer"
                                    className="rounded-full p-1.5 text-white/45 hover:bg-white/12 hover:text-red-400"
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6" />
                                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                      <path d="M10 11v6" /><path d="M14 11v6" />
                                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </nav>

          {/* ── Pied : compte + déconnexion ── */}
          <div className="shrink-0 border-t border-white/8 p-3">
            <button
              onClick={onAccountClick}
              className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 text-sm transition-colors hover:bg-white/6 ${isCollapsed ? 'justify-center' : ''}`}
            >
              <Avatar
                avatarUrl={user?.avatarUrl}
                name={user?.displayName}
                email={user?.email}
                className="h-8 w-8"
                textClassName="text-[10px]"
              />
              {!isCollapsed && (
                <div className="flex min-w-0 flex-col items-start">
                  <span className="max-w-full truncate text-sm font-medium text-white">
                    {user?.displayName || user?.email || 'Mon Compte'}
                  </span>
                  <span className="text-xs text-white/40">{planLabel}</span>
                </div>
              )}
            </button>

            <button
              onClick={() => signOut()}
              title="Se déconnecter"
              className={`mt-0.5 flex w-full items-center rounded-xl py-2 text-sm text-white/45 transition-colors hover:bg-white/6 hover:text-white ${
                isCollapsed ? 'justify-center px-0' : 'gap-3 px-3'
              }`}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </span>
              {!isCollapsed && <span>Déconnexion</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
